import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, Menu, X, Home, Users, Target, BookOpen,
  Brain, Database, TrendingUp, ShoppingCart, Building,
  Trophy, Package, Cpu, Heart, Lightbulb, Zap, Sparkles,
  Rocket, Award, BarChart3, Workflow
} from 'lucide-react';
import '../styles/Header.css';
import logo from '../assets/logo.png';



const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Function to handle navigation to sections
  const handleSectionNavigation = (e, path) => {
    e.preventDefault();
    setActiveDropdown(null);

    const [pathname, hash] = path.split('#');

    // If we're already on the correct page
    if (location.pathname === pathname || (!pathname && hash)) {
      // Just scroll to the section
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } else {
      // Navigate to the page first, then scroll
      navigate(pathname);
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        if (hash) {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 100);
    }
  };

  const navItems = [
    {
      name: 'Home',
      path: '/',
      dropdown: [
        { icon: <Home size={16} />, name: 'Overview', path: '/#overview', description: 'What we do at a glance' },
        { icon: <Target size={16} />, name: 'Why Partner', path: '/#why-partner', description: 'Why organizations choose us' },
        { icon: <Rocket size={16} />, name: 'Orbit Model', path: '/#orbit-model', description: 'Our approach' }
      ]
    },
    {
      name: 'About',
      path: '/about',
      dropdown: [
        { icon: <BookOpen size={16} />, name: 'Our Story', path: '/about#story', description: 'How Kosmoz began' },
        { icon: <Lightbulb size={16} />, name: 'Philosophy', path: '/about#philosophy', description: 'Our guiding principles' },
        { icon: <Users size={16} />, name: 'Team', path: '/about#team', description: 'Meet our experts' },
        { icon: <Zap size={16} />, name: 'How We Work', path: '/about#process', description: 'Our work process' }
      ]
    },
    {
      name: 'Services',
      path: '/services',
      dropdown: [
        {
          icon: <Sparkles size={16} />,
          name: 'AI Advisory & Consulting',
          path: '/services#ai',
          description: 'AI strategy, GenAI, ML systems, and agentic workflows'
        },
        {
          icon: <BarChart3 size={16} />,
          name: 'GTM Strategy & Marketing',
          path: '/services#gtm',
          description: 'Go-to-market strategy and modern marketing engine'
        },
      ]
    },
    {
      name: 'Industries',
      path: '/industries',
      dropdown: [
        { icon: <ShoppingCart size={16} />, name: 'Retail & E-commerce', path: '/industries#retail', description: 'Transform customer experiences' },
        { icon: <Building size={16} />, name: 'Finance & Banking', path: '/industries#finance', description: 'Enhance risk management' },
        { icon: <Trophy size={16} />, name: 'Sports & Entertainment', path: '/industries#sports', description: 'Optimize performance & engagement' },
        { icon: <Package size={16} />, name: 'CPG & Manufacturing', path: '/industries#cpg', description: 'Optimize production & quality' },
        { icon: <Heart size={16} />, name: 'Healthcare', path: '/industries#healthcare', description: 'Improve patient outcomes' },
        { icon: <Cpu size={16} />, name: 'Technology & SaaS', path: '/industries#technology', description: 'Accelerate product development' }
      ]
    },
    {
      name: 'Resources',
      path: '/resources',
      dropdown: [
        { icon: <Brain size={16} />, name: 'AI in Business', path: '/resources#featured', description: 'Implementation models' },
        { icon: <TrendingUp size={16} />, name: 'GTM Strategies', path: '/resources#featured', description: 'Launch mechanics' },
        { icon: <Award size={16} />, name: 'Leadership', path: '/resources#featured', description: 'Decision frameworks' },
        { icon: <Zap size={16} />, name: 'Automation', path: '/resources#featured', description: 'Productivity workflows' }
      ]
    }
  ];

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <nav className="nav-container">
        <Link to="/" className="logo">
          <img
            src={logo}
            alt="Kosmoz Logo"
            className="logo-image"
          />
        </Link>

        {/* Desktop nav: logo (left) + pill (center) + CTA (right) */}
        <div className="nav-center">
          <div className="nav-pill">
            <ul className="nav-list">
              {navItems.map((item) => (
                <li
                  key={item.name}
                  className="nav-item"
                  onMouseEnter={() => setActiveDropdown(item.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link to={item.path} className="nav-link">
                    {item.name}
                    {item.dropdown && <ChevronDown className="nav-arrow" />}
                  </Link>

                  {item.dropdown && (
                    <AnimatePresence>
                      {activeDropdown === item.name && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="dropdown-menu"
                        >
                          {item.dropdown.map((dropItem) => (
                            <a
                              key={dropItem.path}
                              href={dropItem.path}
                              className="dropdown-item"
                              onClick={(e) => handleSectionNavigation(e, dropItem.path)}
                            >
                              <div className="dropdown-icon">{dropItem.icon}</div>
                              <div className="dropdown-content">
                                <span className="dropdown-title">{dropItem.name}</span>
                                {dropItem.description && (
                                  <span className="dropdown-description">{dropItem.description}</span>
                                )}
                              </div>
                            </a>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="nav-actions">
          <Link to="/contact" className="nav-action nav-action-outline">
            Book a Consultation
          </Link>
        </div>

        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X /> : <Menu />}
        </button>
      </nav>

      {/* Mobile slide-in nav */}
      <div className={`mobile-nav ${isMobileMenuOpen ? 'active' : ''}`}>
        <ul className="mobile-nav-list">
          {navItems.map((item) => (
            <li key={item.name} className="mobile-nav-item">
              <div className="mobile-nav-row">
                <Link
                  to={item.path}
                  className="nav-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>

                {item.dropdown && (
                  <button
                    type="button"
                    className="mobile-expand"
                    aria-label={`Toggle ${item.name} menu`}
                    onClick={() =>
                      setActiveDropdown((prev) => (prev === item.name ? null : item.name))
                    }
                  >
                    <ChevronDown className={`nav-arrow ${activeDropdown === item.name ? 'open' : ''}`} />
                  </button>
                )}
              </div>

              {item.dropdown && activeDropdown === item.name && (
                <div className="mobile-dropdown">
                  {item.dropdown.map((dropItem) => (
                    <a
                      key={dropItem.path}
                      href={dropItem.path}
                      className="dropdown-item"
                      onClick={(e) => {
                        handleSectionNavigation(e, dropItem.path);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <div className="dropdown-icon">{dropItem.icon}</div>
                      <div className="dropdown-content">
                        <span className="dropdown-title">{dropItem.name}</span>
                        {dropItem.description && (
                          <span className="dropdown-description">{dropItem.description}</span>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>

        <div className="mobile-actions">
          <Link
            to="/contact"
            className="nav-action nav-action-outline"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Book Strategy Call
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;